/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Toaster } from 'sonner';
import { Icon } from '@/components/icon';
import { TOAST_PRESENTATION } from './toast-types';

/**
 * The single application-level toaster (CRCB2B-641). Rendered once at the root so a
 * toast can be raised from anywhere without the caller mounting a container.
 *
 * Icons come from the shared Material Symbols subset via the map in `toast-types.ts`
 * — previously only `success` had one, leaving the other types iconless. The glyph is
 * what carries type for a user who can't discriminate colour, so every type needs one.
 *
 * Toasts are `unstyled`, which means Sonner emits structure and `data-*` hooks and
 * applies no CSS of its own: the entire visual treatment lives in
 * `src/theme/overrides/sonner.css`. Nothing here is styled unless that file styles it.
 */
export function AppToaster() {
    return (
        <Toaster
            expand
            // Bottom-right, per the in-context Figma examples (10720:113184 / 10720:113191)
            // and confirmed with design. This was `top-center`, which also required an offset
            // that cleared the site header — no longer relevant at the bottom of the viewport,
            // so the offset is now a plain --spacing-4 (16) gutter on every side.
            position="bottom-right"
            closeButton
            className="toaster"
            theme="light"
            offset="1rem"
            icons={{
                // icon/lg (24) per the Intent variants. `fill` defaults to 1, which is
                // what makes these read as solid badges — a filled `check_circle` is a
                // solid disc with the check knocked out, matching the design exactly.
                info: <Icon name={TOAST_PRESENTATION.info.icon} size={24} />,
                success: <Icon name={TOAST_PRESENTATION.success.icon} size={24} />,
                warning: <Icon name={TOAST_PRESENTATION.warning.icon} size={24} />,
                error: <Icon name={TOAST_PRESENTATION.error.icon} size={24} />,
                // Sonner ships its own X; swap in the library glyph. Outlined (fill 0) —
                // the close mark is a stroke in the design, unlike the filled type badges.
                close: <Icon name="close" size={24} fill={false} />,
            }}
            toastOptions={{ unstyled: true }}
        />
    );
}
